import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export type JournalsImportAnalytics = {
  summary: {
    totalLines: number;
    totalJournals: number;
    multiCompanyJournals: number;
    dateRange: { from: string; to: string } | null;
    readyToImport: number;
    blockedJournals: number;
    wouldSkipExisting: number;
    importableAfterSkip: number;
  };
  masterData: {
    companiesInFile: number;
    companiesMatched: number;
    companiesMissing: Array<{ code: string; lineCount: number; journalCount: number }>;
    accountsInFile: number;
    accountsMatched: number;
    accountsMissing: Array<{ code: string; lineCount: number; journalCount: number }>;
    currenciesInFile: string[];
    currenciesMatched: string[];
    currenciesMissing: Array<{ code: string; lineCount: number }>;
    intercompanyGaps: Array<{ companyCode: string; companyName: string; journalCount: number }>;
  };
  periods: {
    years: Array<{ year: number; journalCount: number; hasOpenPeriod: boolean }>;
    journalsInLockedPeriod: number;
  };
  issuesByCategory: Array<{
    category: string;
    label: string;
    journalCount: number;
    lineCount: number;
    samples: Array<{ docNumber: string; docDate: string; detail: string }>;
  }>;
  recommendations: string[];
};

type Props = {
  analytics: JournalsImportAnalytics;
  title?: string;
};

function CoverageBar({ matched, total }: { matched: number; total: number }) {
  const pct = total > 0 ? Math.round((matched / total) * 100) : 0;
  const ok = matched === total && total > 0;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs mb-1">
        <span>{matched} / {total} matched</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', ok ? 'bg-emerald-500' : 'bg-amber-500')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const JournalsImportAnalyticsPanel: React.FC<Props> = ({ analytics, title = 'Validation analysis' }) => {
  const { colors } = useTheme();
  const { summary, masterData, periods, issuesByCategory, recommendations } = analytics;
  const allClear = summary.blockedJournals === 0;

  return (
    <section className={cn('rounded-2xl border shadow-sm overflow-hidden', colors.border, colors.surface)}>
      <div className={cn('px-5 py-4 border-b flex items-center justify-between gap-3', colors.border, colors.surfaceHover)}>
        <h2 className={cn('text-base font-semibold', colors.text)}>{title}</h2>
        <span
          className={cn(
            'text-xs font-medium px-2.5 py-1 rounded-full',
            allClear
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
          )}
        >
          {allClear ? 'Ready to import' : `${summary.blockedJournals} blocked`}
        </span>
      </div>

      <div className="px-5 py-4 space-y-6">
        <ul className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm', colors.textSecondary)}>
          {[
            { label: 'Ready to import', value: summary.readyToImport, valueClass: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Blocked', value: summary.blockedJournals, valueClass: 'text-amber-600 dark:text-amber-400' },
            { label: 'Would skip (existing)', value: summary.wouldSkipExisting, valueClass: colors.text },
            { label: 'Multi-company', value: summary.multiCompanyJournals, valueClass: colors.text },
          ].map(({ label, value, valueClass }) => (
            <li key={label} className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
              <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</span>
              <p className={cn('mt-1 text-lg font-semibold tabular-nums', valueClass)}>{value}</p>
            </li>
          ))}
        </ul>

        {summary.dateRange && (
          <p className={cn('text-sm', colors.textSecondary)}>
            Date range: <strong className={colors.text}>{summary.dateRange.from}</strong>
            {' → '}
            <strong className={colors.text}>{summary.dateRange.to}</strong>
            {' · '}
            {summary.totalLines} lines in {summary.totalJournals} journals
          </p>
        )}

        {recommendations.length > 0 && (
          <div className={cn('rounded-xl border px-4 py-3', colors.border, 'bg-indigo-500/5')}>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>What to fix</h3>
            <ul className={cn('list-disc pl-5 space-y-1 text-sm', colors.textSecondary)}>
              {recommendations.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h3 className={cn('text-sm font-semibold mb-3', colors.text)}>Master data coverage</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Companies (Cmp)</p>
              <CoverageBar matched={masterData.companiesMatched} total={masterData.companiesInFile} />
            </div>
            <div className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Accounts (L4)</p>
              <CoverageBar matched={masterData.accountsMatched} total={masterData.accountsInFile} />
            </div>
            <div className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Currencies</p>
              <p className={cn('mt-2 text-sm', colors.textSecondary)}>
                In file: {masterData.currenciesInFile.join(', ') || '—'}
              </p>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>
                Matched to Currency.code: {masterData.currenciesMatched.join(', ') || '—'}
              </p>
              {masterData.currenciesMissing.length > 0 && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Missing: {masterData.currenciesMissing.map((c) => c.code).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {(masterData.companiesMissing.length > 0 || masterData.accountsMissing.length > 0) && (
          <div className="grid gap-4 lg:grid-cols-2">
            {masterData.companiesMissing.length > 0 && (
              <div>
                <h4 className={cn('text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}>
                  Missing company codes
                </h4>
                <div className={cn('rounded-xl border overflow-hidden text-xs', colors.border)}>
                  <table className="w-full">
                    <thead className={cn(colors.surfaceHover, colors.textMuted)}>
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Code</th>
                        <th className="text-right px-3 py-2 font-medium">Lines</th>
                        <th className="text-right px-3 py-2 font-medium">Journals</th>
                      </tr>
                    </thead>
                    <tbody className={colors.textSecondary}>
                      {masterData.companiesMissing.map((row) => (
                        <tr key={row.code} className={cn('border-t', colors.border)}>
                          <td className={cn('px-3 py-1.5 font-mono', colors.text)}>{row.code}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.lineCount}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.journalCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {masterData.accountsMissing.length > 0 && (
              <div>
                <h4 className={cn('text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}>
                  Missing L4 account codes
                </h4>
                <div className={cn('rounded-xl border overflow-hidden text-xs max-h-48 overflow-y-auto', colors.border)}>
                  <table className="w-full">
                    <thead className={cn(colors.surfaceHover, colors.textMuted, 'sticky top-0')}>
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">L4 Code</th>
                        <th className="text-right px-3 py-2 font-medium">Lines</th>
                        <th className="text-right px-3 py-2 font-medium">Journals</th>
                      </tr>
                    </thead>
                    <tbody className={colors.textSecondary}>
                      {masterData.accountsMissing.map((row) => (
                        <tr key={row.code} className={cn('border-t', colors.border)}>
                          <td className={cn('px-3 py-1.5 font-mono', colors.text)}>{row.code}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.lineCount}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.journalCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {periods.years.length > 0 && (
          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Posting periods by year</h3>
            <div className="flex flex-wrap gap-2">
              {periods.years.map((y) => (
                <span
                  key={y.year}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs',
                    colors.border,
                    y.hasOpenPeriod ? 'bg-emerald-500/10' : 'bg-amber-500/10',
                  )}
                >
                  <span className={cn('font-semibold', colors.text)}>{y.year}</span>
                  <span className={colors.textMuted}>{y.journalCount} journals</span>
                  <span className={y.hasOpenPeriod ? 'text-emerald-600' : 'text-amber-600'}>
                    {y.hasOpenPeriod ? 'open' : 'no open period'}
                  </span>
                </span>
              ))}
            </div>
            {periods.journalsInLockedPeriod > 0 && (
              <p className={cn('mt-2 text-xs text-amber-600 dark:text-amber-400')}>
                {periods.journalsInLockedPeriod} journal(s) fall in a locked or undefined period.
              </p>
            )}
          </div>
        )}

        {masterData.intercompanyGaps.length > 0 && (
          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Intercompany gaps (multi-company)</h3>
            <ul className={cn('text-sm space-y-1', colors.textSecondary)}>
              {masterData.intercompanyGaps.map((g) => (
                <li key={g.companyCode}>
                  <span className={cn('font-medium', colors.text)}>{g.companyName}</span>
                  <span className="opacity-60"> ({g.journalCount} journals)</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {issuesByCategory.length > 0 && (
          <div>
            <h3 className={cn('text-sm font-semibold mb-3', colors.text)}>Issues by type</h3>
            <div className="space-y-3">
              {issuesByCategory.map((issue) => (
                <details
                  key={issue.category}
                  className={cn('rounded-xl border group', colors.border, colors.surfaceHover)}
                >
                  <summary className={cn('cursor-pointer px-4 py-3 text-sm flex items-center justify-between gap-2', colors.text)}>
                    <span>
                      <span className="font-medium">{issue.label}</span>
                      <span className={cn('ml-2 text-xs', colors.textMuted)}>
                        {issue.journalCount} journal(s)
                        {issue.lineCount > 0 ? ` · ${issue.lineCount} line(s)` : ''}
                      </span>
                    </span>
                    <span className={cn('text-xs', colors.textMuted)}>Show examples</span>
                  </summary>
                  {issue.samples.length > 0 && (
                    <ul className={cn('px-4 pb-3 space-y-1.5 text-xs border-t', colors.border, colors.textSecondary)}>
                      {issue.samples.map((s) => (
                        <li key={`${s.docNumber}-${s.docDate}-${s.detail}`}>
                          <span className={cn('font-medium', colors.text)}>Doc {s.docNumber}</span>
                          <span className="opacity-50 mx-1">·</span>
                          {s.docDate}
                          <span className="opacity-50 mx-1">—</span>
                          {s.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default JournalsImportAnalyticsPanel;
